import { useDispatch, useSelector } from 'react-redux';
import { useLocalStorage } from '@uidotdev/usehooks';
import {
  LoginResponseData,
  UpdateUserResponse,
  UserSettingsApiUserSettingsControllerUpdateEmailRequest,
} from '@EHSNavigator/backend';
import OwnerSettings from '../../views/OwnerSettings';
import GeneralSettings from '../../views/GeneralSettings';
import ModalComponent from '../../components/ModalComponent';
import clsx from 'clsx';
import { toggleModal } from '../../reducers/modalToggle';
import EditPersonalDetailsModal from '../../components/EditPersonalDetailsModal';
import { useFormik } from 'formik';
import {
  getChangePasswordSchema,
  getEditPersonalDetailsSchema,
} from './schema';
import { toast } from 'react-toastify';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import { useApiClients } from '../../client/api/useApiClient';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router';
import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';

export default function GeneralSettingsPage() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const verificationAttempted = useRef(false);
  const query = new URLSearchParams(location?.search);
  const userId = query.get('userId');
  const token = query.get('token');

  const [userInfo, setUserInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );
  const [, setNewEmail] = useLocalStorage<string | null>('newEmail', null);

  const [, setMessage] = useLocalStorage<string | null>('message', null);
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );
  const { UserSettingsApi, AuthApi } = useApiClients();

  const INITIAL_VALUES = {
    fullName: userInfo?.user?.fullName,
    email: userInfo?.user?.email,
    contact: userInfo?.user?.contact || '',
    dob:
      userInfo?.user?.dob && moment(userInfo?.user?.dob).isValid()
        ? moment(userInfo?.user?.dob).format('MMMM DD, YYYY')
        : '',
  };
  const CHANGE_PASSWORD = {
    currentPassword: '',
    password: '',
    confirmPassword: '',
  };

  function getChangedValues(obj1: any, obj2: any): Record<string, any> {
    const changed: Record<string, any> = {};
    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
    allKeys.forEach((key) => {
      if (obj1[key] !== obj2[key]) {
        changed[key] = obj1[key];
      }
    });
    return changed;
  }
  const changeUserRoleHandler = useFormik({
    enableReinitialize: true,
    initialValues: INITIAL_VALUES,
    validationSchema: getEditPersonalDetailsSchema(),
    onSubmit: async (values: any) => {
      try {
        const changedFields = getChangedValues(
          {
            fullName: values?.fullName,
            email: values?.email,
            contact: values?.contact || null,
            dob:
              values?.dob && moment(values?.dob)?.isValid()
                ? moment(values?.dob).format('DD/MM/YYYY')
                : null,
          },
          {
            fullName: userInfo?.user?.fullName,
            email: userInfo?.user?.email,
            contact: userInfo?.user?.contact || null,
            dob:
              userInfo?.user?.dob && moment(userInfo?.user?.dob).isValid()
                ? moment(userInfo?.user?.dob).format('DD/MM/YYYY')
                : null,
          },
        );
        const emailExist = await AuthApi.authControllerCheckEmailExist({
          checkEmailExistDTO: {
            email: values?.email,
          },
        });
        if (changedFields?.email && emailExist?.data?.exist === true) {
          changeUserRoleHandler?.setFieldError('email', 'Email already exists');
        } else {
          if (changedFields?.email) {
            setNewEmail(changedFields?.email);
          }
          const res = await UserSettingsApi.userSettingsControllerUpdateProfile(
            {
              id: userInfo?.user?.id as string,
              updateUserSettingDto: {
                ...changedFields,
              },
            },
          );
          setUserInfo((prev: any) => ({
            ...prev,
            user: {
              ...prev?.user,
              ...res?.data?.user,
              org: {
                ...prev?.user?.org,
              },
              role: {
                ...prev?.user?.role,
              },
            },
          }));
          toast.success(res?.data?.message);
          changeUserRoleHandler?.resetForm();
          dispatch(toggleModal(''));
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });
  const changePasswordHandler = useFormik({
    enableReinitialize: true,
    initialValues: CHANGE_PASSWORD,
    validationSchema: getChangePasswordSchema(),
    onSubmit: async (values: any) => {
      try {
        const res =
          await UserSettingsApi.userSettingsControllerUpdateUserPassword({
            userId: userInfo?.user?.id as string,
            updateUserPasswordDto: {
              currentPassword: values?.currentPassword,
              newPassword: values?.password,
            },
          });
        setUserInfo((prev: any) => ({
          ...prev,
          user: {
            ...prev?.user,
            setPasswordAt: res?.data?.user?.setPasswordAt,
            org: {
              ...prev?.user?.org,
            },
            role: {
              ...prev?.user?.role,
            },
          },
        }));
        toast.success(res?.data?.message);
        changePasswordHandler?.resetForm();
        dispatch(toggleModal(''));
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const verifyEmail = useMutation<
    UpdateUserResponse,
    Error,
    UserSettingsApiUserSettingsControllerUpdateEmailRequest
  >({
    mutationFn: async (
      data: UserSettingsApiUserSettingsControllerUpdateEmailRequest,
    ) => {
      const response = await UserSettingsApi.userSettingsControllerUpdateEmail({
        ...data,
      });
      return response.data;
    },

    onSuccess: (data) => {
      setNewEmail(null);
      setUserInfo((prev: any) => ({
        ...prev,
        user: {
          ...prev?.user,
          ...data?.user,
          org: {
            ...prev?.user?.org,
          },
          role: {
            ...prev?.user?.role,
          },
        },
      }));
      setMessage(data?.message);
      navigate('/general-settings');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  useEffect(() => {
    if (userId && token && !verificationAttempted.current) {
      verificationAttempted.current = true;
      verifyEmail.mutate({
        token: token,
        userId: userId,
      });
    }
  }, [userId, token, verifyEmail]);

  const isViewRender =
    userInfo?.user?.role?.name === 'Owner' ? (
      <OwnerSettings />
    ) : (
      <GeneralSettings />
    );
    
  return (
    <div className="relative flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      {isViewRender}
      {(modalName === 'edit-personal-details' ||
        modalName === 'change-password') &&
         (
        <ModalComponent
          otherClasses="px-4 sm:px-0 py-4"
          additionalClass={clsx(
            'w-full max-w-573 rounded-2xl p-5 lg:px-9 lg:py-6  h-fit max-h-full ',
          )}
          customClose={() => {
            changeUserRoleHandler?.resetForm();
            changePasswordHandler?.resetForm();
            dispatch(toggleModal(''));
          }}
        >
          {modalName === 'edit-personal-details' && (
            <EditPersonalDetailsModal
              formHandler={changeUserRoleHandler}
              handleClose={() => {
                changeUserRoleHandler?.resetForm();
                dispatch(toggleModal(''));
              }}
            />
          )}
          {modalName === 'change-password' && (
            <ChangePasswordModal
              formHandler={changePasswordHandler}
              handleClose={() => {
                changePasswordHandler?.resetForm();
                dispatch(toggleModal(''));
              }}
            />
          )}
        </ModalComponent>
      )}
    </div>
  );
}
